import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type {
  ApiResponse,
  BusinessUnit,
  CreateBusinessUnitDto,
  UpdateBusinessUnitDto
} from '@/types';

/**
 * `business_units.id` is a Postgres BIGINT, which pg hands back as a string, so machine
 * serialises it as `"332"` while an inbox's `bu_id` comes through as the number `332`.
 * Matching an inbox to its unit is `inbox.bu_id === bu.id`, and `332 === '332'` is false —
 * left alone, every business unit renders as owning no inboxes at all.
 *
 * Normalised here rather than at each comparison so a new caller cannot reintroduce it,
 * and in this client rather than in machine: machine is the source of truth for candy,
 * kim-ai-backend and the product backends, and its shape is not ours to change. Candy
 * defends itself the same way (`Number(record.id) === Number(buId)` in its store).
 */
const normalize = (bu: BusinessUnit): BusinessUnit => ({ ...bu, id: Number(bu.id) });

export const businessUnitsService = {
  async list(accountId: number): Promise<BusinessUnit[]> {
    const res = await api.get<ApiResponse<{ businessUnits: BusinessUnit[] }>>(
      API_ENDPOINTS.BUSINESS_UNITS.BASE(accountId)
    );
    return res.data.data.businessUnits.map(normalize);
  },

  async create(accountId: number, payload: CreateBusinessUnitDto) {
    const res = await api.post<ApiResponse<{ businessUnit: BusinessUnit }>>(
      API_ENDPOINTS.BUSINESS_UNITS.BASE(accountId),
      payload
    );
    return normalize(res.data.data.businessUnit);
  },

  async update(accountId: number, buId: number, payload: UpdateBusinessUnitDto) {
    const res = await api.patch<ApiResponse<{ businessUnit: BusinessUnit }>>(
      API_ENDPOINTS.BUSINESS_UNITS.BY_ID(accountId, buId),
      payload
    );
    return normalize(res.data.data.businessUnit);
  }
};
